import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { dependsOn, getValue, isPending, trackedAction } from 'mobx-async'

import {
  AvailabilityBlockSource,
  BookingIssue,
  BookingStatus,
  CustomFieldProjection,
  CustomFieldSectionProjection,
  IDate,
  LeadSource,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { Period, PeriodKind } from '@new/__global/components/TapeChart/TapeChart.types'
import { BookingModificationStore } from '@new/domains/bookings/stores/BookingModificationStore'
import { BookingStore } from '@new/domains/bookings/stores/BookingStore'
import {
  BlockedDatesByHomeId,
  getPeriodRangeFromDates,
} from '@new/domains/bookingsTapeChart/BookingsTapeChart.utils'
import { TapeChartBlockStore } from '@new/domains/bookingsTapeChart/stores/TapeChartBlockStore'
import { hotelTapeChartPath } from '@new/domains/hotelTapeChart/config/contants'
import { ExtendedHomeViewTapeChart, HomeDatePrices } from '@new/domains/properties/_types'
import { featuredCustomFieldId } from '@new/domains/properties/screens/propertyInfo/contants'
import { PropertyInfoStore } from '@new/domains/properties/stores/PropertyInfoStore'

import { arriere } from '@arriere/index'
import { getBookingAccessMethods, getPendingAirbnbAlterations } from '@arriere/modules/bookings'
import { getPropertyByIdInTapeChart } from '@arriere/modules/properties'
import { ROUTES } from '@routes'
import { computedRouteParam } from '@stores/computedRouteParam'
import {
  DEFAULT_DATE_RANGE,
  OtherTapeChartFiltersStore,
  TapeChartFiltersStoreBase,
} from '@stores/OtherTapeChartFiltersStore'
import { trackError } from '@tracking/customEventReport'
import { getCustomField, getCustomFieldValue } from '@utils/customFields'

import {
  BookingsTapeChartCacheProps,
  BookingsTapeChartViewType,
  HomesCache,
  Property,
  SimpleBooking,
  TapeChartSidebarFilters,
} from '../BookingsTapeChart.types'
import { PropertyCalendarStayLimits } from '../components/CreateBookingForm/CreateBookingForm.types'
import { CreateBookingStore } from './CreateBookingStore'

class _BookingsTapeChartStore
  extends OtherTapeChartFiltersStore
  implements TapeChartFiltersStoreBase
{
  constructor() {
    super([ROUTES.revenueBookingsTapeChart.path])

    makeObservable(this, {
      isTapeChartScreenOpen: observable,
      isLoading: observable,
      blockedDatesByHomeId: observable,
      blockedDatesWithSingleNight: observable,
      bookingTerm: observable,
      reloadBookingsToggle: observable,
      isFiltersSidebarOpen: observable,
      isSearchingForBookings: observable,
      selectedPeriod: observable,
      currentHomeId: observable,
      blockPeriodsCache: observable,
      currentBookingId: observable,
      propertiesColumnWidthOffset: observable,
      bookingIssuesFiltered: observable,
      showPreviewPeriod: observable,
      periodActionsMenuIsOpen: observable,
      cacheObject: observable,
      loadedIds: observable,
      pagination: observable,
      pages: observable,
      searchTermPagination: observable,
      propertyCalendarStayLimits: observable,
      hasEmptyResults: computed,
      isClearBasicFiltersDisabled: computed,
      isClearOtherFiltersDisabled: computed,
      isClearAllFiltersDisabled: computed,
      sidebarFiltersFromUrl: computed,
      allHighlightsList: computed({ keepAlive: true }),
      highlightsOptions: computed,
      allFilterableAmenitiesList: computed({ keepAlive: true }),
      amenitiesOptions: computed({ keepAlive: true }),
      tapeChartCounters: computed,
      allHomes: computed,
      homes: computed,
      allPeriods: computed,
      bookings: computed,
      blockedPeriods: computed,
      searchTermBookings: computed,
      bookingPeriods: computed,
      blockedPeriodsForTapeChart: computed,
      selectedHome: computed,
      selectBookingIssuesFiltersEnabled: computed,
      selectedReservation: computed,
      selectedReservationLockCodes: computed,
      pendingAirbnbAlteration: computed,
      priceByDates: computed,
      setPropertyCalendarStayLimit: action.bound,
      setLoadedIds: action.bound,
      setBlockedDatesByHomeId: action.bound,
      setBlockedDatesWithSingleNight: action.bound,
      setHomesCache: action.bound,
      setPeriodsCache: action.bound,
      setHomePricesCache: action.bound,
      setQuantityOfBookingIssues: action.bound,
      setAvailableDatesFilter: action.bound,
      setIsTapeChartScreenOpen: action.bound,
      setPages: action.bound,
      setIsLoading: action.bound,
      setTermFilter: action.bound,
      setBookingTerm: action.bound,
      setDateRangeFilter: override,
      setRegionIdsFilter: action.bound,
      setSeasonGroupIdsFilter: override,
      setReservationIdsFilter: action.bound,
      setPaymentIdsFilter: action.bound,
      setShowOffBoardedFilter: action.bound,
      setCurrentHomeId: action.bound,
      setCurrentBookingId: action.bound,
      setViewTypeFilter: action.bound,
      clearBasicFilters: action.bound,
      clearOtherFilters: action.bound,
      clearAllFilters: action.bound,
      setBookingIdParam: action.bound,
      setPropertyIdParam: action.bound,
      setBlockIdParam: action.bound,
      setLeadSourcesParam: action.bound,
      setActiveFiltersLength: override,
      toggleFiltersSidebarOpen: action.bound,
      toggleTermSearch: action.bound,
      setSelectedPeriod: action.bound,
      setSearchTermPagination: action.bound,
      setPagination: action.bound,
      setShowPreviewPeriod: action.bound,
      setPeriodActionsMenuIsOpen: action.bound,
      setPropertiesColumnWidthOffset: action.bound,
      resetPagination: action.bound,
      resetCachedHomes: action.bound,
      resetSelectedBooking: action.bound,
      applyCachedFilters: action.bound,
    })

    this.getPropertyStayLimits = trackedAction<any>(this.getPropertyStayLimits.bind(this))
  }

  isTapeChartScreenOpen = false

  // Controls re-rendering when we update some data, use as you need with toggle function below file
  isLoading = true

  blockedDatesByHomeId?: BlockedDatesByHomeId = undefined

  blockedDatesWithSingleNight?: BlockedDatesByHomeId = undefined

  bookingTerm: string | undefined = ''

  reloadBookingsToggle = true

  isFiltersSidebarOpen = false

  isSearchingForBookings = false

  selectedPeriod: { id: string; propertyId: string } | undefined = undefined

  currentHomeId: string | undefined = ''

  blockPeriodsCache: Period[] = []

  currentBookingId: string | undefined = ''

  propertiesColumnWidthOffset = 0

  bookingIssuesFiltered = {}

  showPreviewPeriod = false

  periodActionsMenuIsOpen = false

  cacheObject: BookingsTapeChartCacheProps = {
    homes: [],
    homePrices: [],
    periods: [],
  }

  loadedIds: string[] = []

  pagination = { page: 1, size: 100 }

  pages = 0

  searchTermPagination = { page: 1, size: 30 }

  propertyCalendarStayLimits: PropertyCalendarStayLimits | undefined = undefined

  availableDatesFilterParam = computedRouteParam<string | undefined>('availableDates', {
    patterns: [ROUTES.revenueBookingsTapeChart.path],
  })

  termFilterParam = computedRouteParam<string | undefined>('term', {
    patterns: [ROUTES.revenueBookingsTapeChart.path],
  })

  dateRangeFilterParam = computedRouteParam<string>('date', {
    patterns: [ROUTES.revenueBookingsTapeChart.path],
    defaultValue: DEFAULT_DATE_RANGE,
  })

  regionIdsFilterParam = computedRouteParam<string[] | undefined>('markets', {
    patterns: [ROUTES.revenueBookingsTapeChart.path],
    format: (values?: string[] | null) => (values ? values.join(',') : ''),
    parse: valueString => (valueString ? valueString.split(',') : undefined),
    defaultValue: undefined,
  })

  reservationIdsFilterParam = computedRouteParam<string[] | undefined>('reservation', {
    patterns: [ROUTES.tapeChart.path],
    format: (values?: string[] | null) => (values ? values.join(',') : ''),
    parse: valueString => (valueString ? valueString.split(',') : undefined),
  })

  paymentIdsFilterParam = computedRouteParam<string[] | undefined>('payment', {
    patterns: [ROUTES.tapeChart.path],
    format: (values?: string[] | null) => (values ? values.join(',') : ''),
    parse: valueString => (valueString ? valueString.split(',') : undefined),
  })

  showOffBoardedParam = computedRouteParam<boolean | undefined>('onlyAvailable', {
    patterns: [ROUTES.tapeChart.path],
    parse: JSON.parse,
    format: JSON.stringify,
    defaultValue: false,
  })

  viewTypeFilterParam = computedRouteParam<string | undefined>('viewType', {
    patterns: [ROUTES.tapeChart.path],
    defaultValue: BookingsTapeChartViewType.reservation,
  })

  bookingIdParam = computedRouteParam<string | undefined>('booking-id', {
    patterns: [
      ROUTES.tapeChart.path,
      ROUTES.fastTapeChart.path,
      ROUTES.revenueBookingsTapeChart.path,
      ROUTES.bookingPaymentAndServices.path,
      ROUTES.bookingPaymentDetails.path,
      ROUTES.bookingValueAddedServices.path,
      ROUTES.bookingMessages.path,
      ROUTES.bookingGuests.path,
      ROUTES.bookingCases.path,
      ROUTES.bookingGuestDamage.path,
      ROUTES.bookingUserSessions.path,
      ROUTES.bookingAccountingTimeline.path,
      ROUTES.bookingAgreements.path,
      ROUTES.bookingReviews.path,
      ROUTES.bookingHistory.path,
      ROUTES.bookingInfo.path,
      ROUTES.bookingSMSConversations.path,
      ROUTES.bookingTimeline.path,
      ...hotelTapeChartPath,
    ],
  })

  propertyIdParam = computedRouteParam<string | undefined>('property-id', {
    patterns: [
      ROUTES.fastTapeChart.path,
      ROUTES.tapeChart.path,
      ROUTES.revenueBookingsTapeChart.path,
      ...hotelTapeChartPath,
    ],
  })

  blockIdParam = computedRouteParam<string | undefined>('block-id', {
    patterns: [
      ROUTES.fastTapeChart.path,
      ROUTES.tapeChart.path,
      ROUTES.revenueBookingsTapeChart.path,
      ...hotelTapeChartPath,
    ],
  })

  leadSourceParam = computedRouteParam<string[] | undefined>('lead-sources', {
    patterns: [ROUTES.revenueBookingsTapeChart.path],
    format: (values?: string[] | null) => (values ? values.join(',') : ''),
    parse: valueString => (valueString ? valueString.split(',') : undefined),
  })

  get hasEmptyResults() {
    return this.loadedIds.length === 0 && !isPending(this.allHomes)
  }

  get isClearBasicFiltersDisabled(): boolean {
    return (
      (this.termFilterParam.get() === undefined || this.termFilterParam.get() === '') &&
      this.dateRangeFilterParam.get() === DEFAULT_DATE_RANGE &&
      this.regionIdsFilterParam.get() === undefined &&
      this.reservationIdsFilterParam.get() === undefined &&
      (this.showOffBoardedParam.get() === undefined || this.showOffBoardedParam.get() === false) &&
      this.bookingIdParam.get() === undefined &&
      this.propertyIdParam.get() === undefined &&
      this.showNightlyRateFilterParam.get() === false
    )
  }

  get isClearOtherFiltersDisabled(): boolean {
    return (
      this.minBedroomsFilterParam.get() === undefined &&
      this.minBathroomsFilterParam.get() === undefined &&
      this.petsFilterParam.get() === undefined &&
      this.amenityIdsFilterParam.get() === undefined &&
      this.availableDatesFilterParam.get() === undefined &&
      this.highlightIdsFilterParam.get() === undefined &&
      this.minPriceFilterParam.get() === undefined &&
      this.maxPriceFilterParam.get() === undefined &&
      this.revenueModelFilterParam.get() === undefined &&
      this.paymentIdsFilterParam.get() === undefined &&
      this.riskLevelFilterParam.get() === undefined &&
      this.bookingIssuesFilterParam.get() === undefined &&
      (this.depositsOnHoldParam.get() === undefined || this.depositsOnHoldParam.get() === false)
    )
  }

  get isClearAllFiltersDisabled(): boolean {
    return this.isClearBasicFiltersDisabled && this.isClearOtherFiltersDisabled
  }

  get sidebarFiltersFromUrl(): Promise<TapeChartSidebarFilters> {
    const filters = {
      ...this.otherTapeChartFiltersFromUrl,
      availableDates: this.availableDatesFilterParam.get(),
      nightlyRate: this.showNightlyRateFilterParam.get(),
      offBoardedHomes: this.showOffBoardedParam.get(),
      paymentStatus: this.paymentIdsFilterParam.get(),
      period: this.dateRangeFilterParam.get(),
      regions: this.regionIdsFilterParam.get(),
      reservationStatus: this.reservationIdsFilterParam.get(),
      seasonGroupsIds: this.seasonGroupsFilterParam.get(),
      exactBedrooms: this.exactBedroomsFilterParam.get(),
    }

    return Promise.resolve().then(() => {
      this.setFiltersCache(filters)
      return filters
    })
  }

  get allHighlightsList(): Promise<{ id: string; name: string }[]> {
    return arriere.properties
      .getHighlightsList()
      .then(response => response.sort((a, b) => a.name.localeCompare(b.name)))
  }

  get highlightsOptions() {
    return this.allHighlightsList.then(
      highlights =>
        highlights?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [],
    )
  }

  get allFilterableAmenitiesList(): Promise<{ id: string; name: string }[]> {
    return arriere.properties
      .getFilterableAmenitiesList()
      .then(response => response.sort((a, b) => a.name.localeCompare(b.name)))
  }

  get amenitiesOptions() {
    return this.allFilterableAmenitiesList.then(
      amenities =>
        amenities?.map(({ id, name }) => ({
          value: id,
          hasFullWidth: true,
          label: name,
        })) || [],
    )
  }

  get tapeChartCounters() {
    const regionIds = this.regionIdsFilterParam.get()
    const name = this.termFilterParam.get()
    const amenityIds = this.amenityIdsFilterParam.get()
    const minBedrooms = this.minBedroomsFilterParam.get()
      ? Number(this.minBedroomsFilterParam.get()?.replace(/\D/g, ''))
      : undefined
    const minBathrooms = this.minBathroomsFilterParam.get()
      ? Number(this.minBathroomsFilterParam.get()?.replace(/\D/g, ''))
      : undefined
    const petPolicy = this.petsFilterParam.get()
    const availableDatesFilter = this.availableDatesFilterParam.get()
    const [checkIn, checkOut] = availableDatesFilter
      ? availableDatesFilter.split('_')
      : [undefined, undefined]
    const minPrice = this.minPriceFilterParam.get()
      ? Number(this.minPriceFilterParam.get())
      : undefined
    const maxPrice = this.maxPriceFilterParam.get()
      ? Number(this.maxPriceFilterParam.get())
      : undefined
    const riskLevel = this.riskLevelFilterParam.get()
    const revenueModel = this.revenueModelFilterParam.get()

    return arriere.properties.getTapeChartPropertyFilterCounters({
      regionIds,
      name,
      checkIn,
      checkOut,
      minPrice,
      maxPrice,
      petPolicy,
      minBathrooms,
      minBedrooms,
      amenityIds,
      riskLevel,
      revenueModel,
    })
  }

  get allHomes(): Promise<Property[]> {
    const regionIds = this.regionIdsFilterParam.get()
    const name = this.termFilterParam.get()
    const seasonGroupIds = this.seasonGroupsFilterParam.get()
    const amenityIds = this.amenityIdsFilterParam.get()
    const exactBedrooms = this.exactBedroomsFilterParam.get()
      ? Number(this.exactBedroomsFilterParam.get()?.replace(/\D/g, ''))
      : undefined
    const highlightIds = this.highlightIdsFilterParam.get()
    const minBedrooms = this.minBedroomsFilterParam.get()
      ? Number(this.minBedroomsFilterParam.get()?.replace(/\D/g, ''))
      : undefined
    const minBathrooms = this.minBathroomsFilterParam.get()
      ? Number(this.minBathroomsFilterParam.get()?.replace(/\D/g, ''))
      : undefined
    const petPolicy = this.petsFilterParam.get()
    const availableDatesFilter = this.availableDatesFilterParam.get()
    const [checkIn, checkOut] = availableDatesFilter
      ? availableDatesFilter.split('_')
      : [undefined, undefined]
    const minPrice = this.minPriceFilterParam.get()
      ? Number(this.minPriceFilterParam.get())
      : undefined
    const maxPrice = this.maxPriceFilterParam.get()
      ? Number(this.maxPriceFilterParam.get())
      : undefined
    const riskLevel = this.riskLevelFilterParam.get()
    const revenueModel = this.revenueModelFilterParam.get()
    const withOffboarded = this.showOffBoardedParam.get()

    return arriere.properties
      .getAllPropertiesForTapeChart({
        regionIds,
        ...(!this.isSearchingForBookings && { name }),
        ...(this.isSearchingForBookings && {
          homeIds: this.propertyIdParam.get() ? [this.propertyIdParam.get() as string] : undefined,
        }),
        checkIn,
        checkOut,
        minPrice,
        maxPrice,
        petPolicy,
        minBathrooms,
        minBedrooms,
        exactBedrooms,
        amenityIds,
        highlightIds,
        riskLevel,
        revenueModel,
        withOffboarded,
        pagination: this.pagination,
        seasonGroupIds,
      })
      .then(response => {
        const resultsData = response.data.results as Property[]
        const resultsHomeIds = resultsData.map(homes => homes.id)
        this.setPages(response.data.pages || 0)
        this.setLoadedIds(
          this.pagination.page === 1
            ? resultsHomeIds
            : [...new Set([...this.loadedIds, ...resultsHomeIds])],
        )

        this.setHomesCache({ data: resultsData, ...this.pagination })
        return resultsData
      })
  }

  get homes() {
    dependsOn(this.showOffBoardedParam)
    const { homes } = this.cacheObject

    const cachedHomes = homes.toSorted((a, b) => a.page - b.page).flatMap(({ data }) => data)

    return cachedHomes
  }

  get allPeriods(): Promise<Period[]> {
    return Promise.all([this.bookingPeriods, this.blockedPeriods]).then(results => {
      const [resultsBookingsPeriods] = results
      const periods = [resultsBookingsPeriods, this.blockPeriodsCache].flatMap(it => it || [])

      if (periods) {
        this.setPeriodsCache(periods)
      }

      return periods
    })
  }

  get bookings(): Promise<SimpleBooking> {
    dependsOn(
      TapeChartBlockStore.cancelTapeChartBlock,
      TapeChartBlockStore.editTapeChartBlock,
      TapeChartBlockStore.createTapeChartBlock,
      BookingModificationStore.alterBooking,
      CreateBookingStore.createManualBooking,
    )

    const { from, to } = getPeriodRangeFromDates(this.dateRangeFilterParam.get())
    const regionIdsFilter = this.regionIdsFilterParam.get()
    const status = this.reservationIdsFilterParam.get() as BookingStatus[]

    if (this.loadedIds.length || this.propertyIdParam.get()) {
      return arriere.bookings.getBookingsForTapeChart({
        regionIds: regionIdsFilter,
        from,
        to,
        homeIds:
          this.isSearchingForBookings && this.propertyIdParam.get()
            ? [this.propertyIdParam.get() as string]
            : this.loadedIds,
        status,
      })
    }

    return Promise.resolve({ results: [] })
  }

  get blockedPeriods(): Promise<Period[]> {
    dependsOn(
      TapeChartBlockStore.cancelTapeChartBlock,
      TapeChartBlockStore.editTapeChartBlock,
      TapeChartBlockStore.createTapeChartBlock,
    )

    const { from, to } = getPeriodRangeFromDates(this.dateRangeFilterParam.get())
    const homeIds = this.loadedIds
    if (homeIds.length) {
      return arriere.properties
        .propertyBlockedPeriods({
          homeIds,
          from,
          to,
        })
        .then(blockedPeriods => {
          const resultsParsed = blockedPeriods
            // we already differentiate a block by eci/lco by detailedCheckin/detailedCheckout custom time
            .filter(period => period.source !== AvailabilityBlockSource.earlyCheckIn)
            .filter(period => period.source !== AvailabilityBlockSource.lateCheckOut)
            .map(period => {
              return {
                homeId: period.homeId,
                id: period.id,
                startDate: period.period.start,
                endDate: period.period.end,
                kind: PeriodKind.block,
                reason: period.source,
                createdBy: period.createdBy ?? '',
                createdAt: period.createdAt ?? '',
                updatedAt: period.updatedAt ?? '',
                updatedBy: period.updatedBy ?? '',
                note: period.note ?? '',
                activeServicesCount: 0,
                parent: period.parent,
                notificationSetting: period?.notificationSetting,
              }
            })
          this.blockPeriodsCache = resultsParsed
          return resultsParsed
        })
    }

    return Promise.resolve(this.blockPeriodsCache)
  }

  get searchTermBookings() {
    const { searchTermPagination, isSearchingForBookings, termFilterParam, bookingTerm } = this
    const searchTerm = termFilterParam.get() || bookingTerm

    if (searchTerm && isSearchingForBookings) {
      return arriere.bookings.searchTermBookings({
        filter: searchTerm,
        pagination: searchTermPagination,
      })
    }

    return Promise.resolve({ pages: 0, results: [] })
  }

  get bookingPeriods(): Promise<Period[]> {
    return this.bookings.then(response =>
      response.results.map(booking => {
        // Flags custom field
        const flags = getCustomFieldValue(
          booking.customFields as CustomFieldSectionProjection[],
          '4cb18b50-2e1d-4a42-859b-d075450e6bc5',
        )

        return {
          homeId: booking.homeId,
          id: booking.id,
          startDate: booking.checkIn,
          endDate: booking.checkOut,
          kind: PeriodKind.booking,
          guestName: booking.guestName,
          issues: booking.issues,
          home: {
            links: booking.home.links,
          },
          hash: booking.hash,
          source: booking.source,
          flags: flags ? flags[0] : undefined,
          eci: booking.detailedCheckIn.custom?.time,
          lco: booking.detailedCheckOut.custom?.time,
          note: booking.notes,
          securityDepositStatus: booking.securityDepositStatus,
          paymentBalanceStatus: booking.folio.summary.paymentBalanceStatus,
          activeServicesCount: booking.activeServicesCount,
          folio: booking.folio,
        }
      }),
    )
  }

  get blockedPeriodsForTapeChart(): Promise<Period[]> {
    dependsOn(
      TapeChartBlockStore.cancelTapeChartBlock,
      TapeChartBlockStore.editTapeChartBlock,
      TapeChartBlockStore.createTapeChartBlock,
      this.setCurrentHomeId,
      CreateBookingStore.createManualBooking,
    )

    const { from, to } = getPeriodRangeFromDates(this.dateRangeFilterParam.get())

    if (!this.currentHomeId || !this.currentHomeId.length) {
      return Promise.resolve([])
    }

    return arriere.properties
      .propertyBlockedPeriods({
        homeIds: [this.currentHomeId],
        from,
        to,
      })
      .then(blockedPeriods =>
        blockedPeriods
          ?.filter(period => period.source !== AvailabilityBlockSource.earlyCheckIn)
          .filter(period => period.source !== AvailabilityBlockSource.lateCheckOut)
          .map(period => ({
            homeId: period.homeId,
            id: period.id,
            startDate: period.period.start,
            endDate: period.period.end,
            kind: PeriodKind.block,
            reason: period.source,
            createdBy: period.createdBy ?? '',
            createdAt: period.createdAt ?? '',
            updatedAt: period.updatedAt ?? '',
            updatedBy: period.updatedBy ?? '',
            note: period.note ?? '',
            activeServicesCount: 0,
            parent: period.parent,
          })),
      )
  }

  get selectedHome(): Promise<ExtendedHomeViewTapeChart | undefined> {
    dependsOn(this.blockedPeriodsForTapeChart, this.setCurrentHomeId)

    const { from, to } = getPeriodRangeFromDates(this.dateRangeFilterParam.get())

    if (this.currentHomeId) {
      return getPropertyByIdInTapeChart({
        homeId: this.currentHomeId,
        from,
        to,
      })
    }

    return Promise.resolve(undefined)
  }

  get selectBookingIssuesFiltersEnabled() {
    const bookingIssues = this.bookingIssuesFilterParam.get()

    return bookingIssues
  }

  get selectedReservation() {
    void [BookingStore.forceUpdateBookingInfo]
    dependsOn(BookingStore.alterBookingDetails)
    const bookingId = this.bookingIdParam.get()

    return arriere.bookings.getBookingById(bookingId)
  }

  get selectedReservationLockCodes() {
    dependsOn(BookingStore.alterBookingDetails)
    const bookingId = this.bookingIdParam.get()

    if (bookingId) {
      return getBookingAccessMethods(bookingId)
    }

    return Promise.resolve(undefined)
  }

  get pendingAirbnbAlteration() {
    dependsOn(
      BookingStore.alterAirbnbBooking,
      BookingStore.acceptGuestAirbnbAlterationRequest,
      BookingStore.cancelHostAirbnbAlterationRequest,
      BookingStore.declineGuestAirbnbAlterationRequest,
    )

    const bookingInfo = getValue(this.selectedReservation)

    if (!bookingInfo) {
      return Promise.resolve(undefined)
    }

    if (bookingInfo.source === LeadSource.airBnb && bookingInfo.otaBookingInfo?.otaBookingId) {
      return getPendingAirbnbAlterations({
        bookingId: bookingInfo.id,
        confirmationCode: bookingInfo.otaBookingInfo.otaBookingId,
        homeId: bookingInfo.homeId,
      })
        .then(async response => {
          if (response?.metadata?.changes?.listingId?.to) {
            await BookingStore.getAlterationRelocationHome({
              homeId: response.payload?.homeId,
              isFromHotel: bookingInfo.isFromHotel,
            })
          }
          return response
        })
        .catch(error => {
          Alert.Error(
            'An error occurred when getting pending airbnb alteration. Please try again..',
          )
          trackError(
            'Booking Airbnb Alteration: An error occurred when getting airbnb alteration info.',
            { error },
          )
          return undefined
        })
    }

    return Promise.resolve(undefined)
  }

  get priceByDates(): Promise<void> {
    const { from, to } = getPeriodRangeFromDates(this.dateRangeFilterParam.get())
    const homeIds = this.loadedIds

    if (homeIds.length) {
      return arriere.properties
        .propertyDatedPrices({
          homeIds,
          from,
          to,
        })
        .then(result => this.setHomePricesCache(result))
    }

    return Promise.resolve(undefined)
  }

  async getPropertyStayLimits(from: IDate, to: IDate) {
    const homeId = this.currentHomeId
    if (!homeId) return Promise.resolve(undefined)
    return arriere.properties
      .getPropertyStayLimit(homeId, from, to)
      .then(stayLimit => this.setPropertyCalendarStayLimit(stayLimit))
  }

  setPropertyCalendarStayLimit = (data?: PropertyCalendarStayLimits) => {
    this.propertyCalendarStayLimits = data
  }

  setLoadedIds = (homes: string[]): void => {
    this.loadedIds = homes
  }

  setBlockedDatesByHomeId = (blockedDatesByHomeId?: BlockedDatesByHomeId): void => {
    this.blockedDatesByHomeId = blockedDatesByHomeId
  }

  setBlockedDatesWithSingleNight = (blockedDatesWithSingleNight?: BlockedDatesByHomeId): void => {
    this.blockedDatesWithSingleNight = blockedDatesWithSingleNight
  }

  setHomesCache = (homes: HomesCache): void => {
    if (this.pagination.page === 1) {
      this.cacheObject.homes = [homes]
    } else {
      this.cacheObject.homes = [...new Set([...this.cacheObject.homes, homes])]
    }
  }

  setPeriodsCache = (periods: Period[]): void => {
    this.cacheObject.periods = periods
    this.setQuantityOfBookingIssues(periods)
  }

  setHomePricesCache = (prices: HomeDatePrices[]): void => {
    const newPrices =
      this.pagination.page === 1
        ? prices
        : [...new Set([...this.cacheObject.homePrices, ...prices])]

    this.cacheObject.homePrices = newPrices
  }

  setQuantityOfBookingIssues = (periods: Period[]): void => {
    const bookingIssuesQuantity: Record<string, number> = {}
    const bookingIssuesValues = Object.keys(BookingIssue)

    if (isEmpty(bookingIssuesQuantity)) {
      bookingIssuesValues.forEach(bookingIssueValue => {
        bookingIssuesQuantity[
          BookingIssue[
            bookingIssueValue as keyof typeof BookingIssue
          ] as keyof typeof bookingIssuesQuantity
        ] = 0
      })
    }

    periods.forEach(period => {
      period.issues?.forEach(issue => {
        bookingIssuesQuantity[issue] += 1
      })
    })

    this.bookingIssuesFiltered = bookingIssuesQuantity
  }

  setAvailableDatesFilter = (date?: string): void => {
    this.availableDatesFilterParam.replace(date || undefined)
  }

  setIsTapeChartScreenOpen = (value: boolean) => {
    this.isTapeChartScreenOpen = value
  }

  setPages = (pages: number): void => {
    this.pages = pages
  }

  setIsLoading = (value: boolean): void => {
    this.isLoading = value
  }

  setTermFilter = (nameFilter?: string): void => {
    this.resetCachedHomes()
    this.resetPagination()
    this.termFilterParam.replace(nameFilter || undefined)
  }

  setBookingTerm = (bookingTerm?: string): void => {
    this.bookingTerm = bookingTerm
  }

  setDateRangeFilter(date?: string): void {
    this.dateRangeFilterParam.replace(date || undefined)
  }

  setRegionIdsFilter = (regionIds?: string[]): void => {
    this.resetCachedHomes()
    this.resetPagination()
    this.regionIdsFilterParam.replace(regionIds)
  }

  setSeasonGroupIdsFilter(regionIds?: string[]): void {
    this.resetCachedHomes()
    this.resetPagination()
    this.seasonGroupsFilterParam.replace(regionIds)
  }

  setReservationIdsFilter = (reservationIds?: string[]): void => {
    this.reservationIdsFilterParam.replace(reservationIds || undefined)
  }

  setPaymentIdsFilter = (paymentIds?: string[]): void => {
    this.resetPagination()
    this.paymentIdsFilterParam.replace(paymentIds || undefined)
  }

  setShowOffBoardedFilter = (value?: boolean): void => {
    this.showOffBoardedParam.replace(value)
  }

  setCurrentHomeId = (homeId?: string): void => {
    this.currentHomeId = homeId
  }

  setCurrentBookingId = (bookingId?: string): void => {
    this.currentBookingId = bookingId
  }

  setViewTypeFilter = (viewType?: string): void => {
    this.viewTypeFilterParam.replace(viewType || undefined)
  }

  clearBasicFilters = (): void => {
    this.setTermFilter('')
    this.setRegionIdsFilter(undefined)
    this.setReservationIdsFilter(undefined)
    this.setDateRangeFilter(undefined)
  }

  clearOtherFilters() {
    this.superClearOtherFilters()

    this.setAvailableDatesFilter(undefined)
    this.setPaymentIdsFilter(undefined)
    this.setShowOffBoardedFilter(undefined)
  }

  clearAllFilters(): void {
    this.clearBasicFilters()
    this.clearOtherFilters()
    this.resetPagination()
  }

  setBookingIdParam = (bookingId?: string): void => {
    this.bookingIdParam.replace(bookingId || undefined)
    this.setCurrentBookingId(bookingId)
    this.blockIdParam.replace(undefined)
  }

  setPropertyIdParam = (propertyId?: string): void => {
    this.propertyIdParam.replace(propertyId || undefined)
    this.currentHomeId = propertyId
  }

  setBlockIdParam = (blockId?: string): void => {
    this.blockIdParam.replace(blockId || undefined)
    this.setBookingIdParam(undefined)
  }

  setLeadSourcesParam = (leadSources?: string[]): void => {
    this.leadSourceParam.replace(leadSources || undefined)
  }

  setActiveFiltersLength(value: number) {
    this.activeFiltersLength = value
  }

  toggleFiltersSidebarOpen(): void {
    this.isFiltersSidebarOpen = !this.isFiltersSidebarOpen
  }

  toggleTermSearch(value: boolean): void {
    this.isSearchingForBookings = value
  }

  setSelectedPeriod = (period: { id: string; propertyId: string } | undefined): void => {
    this.selectedPeriod = period
  }

  setSearchTermPagination = (page: number): void => {
    this.searchTermPagination = { page: page + 1, size: this.searchTermPagination.size }
  }

  setPagination = (page: number): void => {
    this.pagination = { page, size: this.pagination.size }
  }

  setShowPreviewPeriod = (shouldShowPreview: boolean): void => {
    this.showPreviewPeriod = shouldShowPreview
  }

  setPeriodActionsMenuIsOpen = (periodActionsMenuIsOpen: boolean): void => {
    this.periodActionsMenuIsOpen = periodActionsMenuIsOpen
  }

  setPropertiesColumnWidthOffset = (newWidth: number) => {
    this.propertiesColumnWidthOffset = newWidth
  }

  resetPagination = (): void => {
    this.resetCachedHomes()
    if (this.pagination.page !== 1) {
      this.pagination = { page: 1, size: this.pagination.size }
    }
  }

  resetCachedHomes = (): void => {
    this.cacheObject.homes = []
  }

  resetSelectedBooking = (): void => {
    this.setSelectedPeriod(undefined)
    this.setBookingIdParam(undefined)
    this.setPropertyIdParam(undefined)
  }

  applyCachedFilters() {
    this.superApplyCachedFilters()

    const filtersValueFromUrl = getValue(this.sidebarFiltersFromUrl)

    if (!isEqual(this.filtersCache.availableDates, filtersValueFromUrl?.availableDates))
      this.setAvailableDatesFilter(this.filtersCache.availableDates)

    if (!isEqual(this.filtersCache.seasonGroupsIds, filtersValueFromUrl?.seasonGroupsIds))
      this.setSeasonGroupIdsFilter(this.filtersCache.seasonGroupsIds)

    if (!isEqual(this.filtersCache.paymentStatus, filtersValueFromUrl?.paymentStatus))
      this.setPaymentIdsFilter(this.filtersCache.paymentStatus)

    if (!isEqual(this.filtersCache.regions, filtersValueFromUrl?.regions))
      this.setRegionIdsFilter(this.filtersCache.regions)

    if (!isEqual(this.filtersCache.reservationStatus, filtersValueFromUrl?.reservationStatus))
      this.setReservationIdsFilter(this.filtersCache.reservationStatus)

    if (!isEqual(this.filtersCache.offBoardedHomes, filtersValueFromUrl?.offBoardedHomes))
      this.setShowOffBoardedFilter(this.filtersCache.offBoardedHomes)
  }
}

export const BookingsTapeChartStore = new _BookingsTapeChartStore()
